import { Link } from "react-router-dom";
import {
    ArrowRight,
    Sparkles,
    Sprout,
    HandCoins,
    Handshake,
    Search,
    Package,
    Wallet,
    Recycle,
    Users,
    ShieldCheck,
    BadgeCheck,
    Lock,
} from "lucide-react";
import { LANDING } from "../lib/testIds";
import { BRAND, LOGO_URL } from "../lib/brand";

const STEPS = [
    {
        icon: Search,
        kicker: "Step 01",
        title: "Browse items nearby",
        body: "Search by what you need — drills, tents, mixers, ladders. Every item belongs to a neighbor on your block, not a warehouse.",
    },
    {
        icon: Package,
        kicker: "Step 02",
        title: "Borrow from neighbors",
        body: "Send a request, pick it up, and use it for the day or the week. Pay a fair daily price — no overnight shipping, no buying things you'll use twice.",
    },
    {
        icon: Wallet,
        kicker: "Step 03",
        title: "Lend from your garage",
        body: "Got a power washer collecting dust? Turn it into income. List it once and let it pay for itself, one loan at a time.",
    },
];

const BENEFITS = [
    {
        icon: Wallet,
        title: "Save real money",
        body: "Why spend $200 on a tool you'll use twice? Borrow it for $6. Why let your gear collect dust? Lend it out and earn from it instead.",
    },
    {
        icon: Recycle,
        title: "Reduce waste",
        body: "One drill on the block instead of ten. Fewer things bought, fewer things tossed — Lendly is built for the planet, not the landfill.",
    },
    {
        icon: Users,
        title: "Build community",
        body: "Every borrow-and-return is a small handshake. You'll know more of your neighbors after a week on Lendly than in a year of nodding from the driveway.",
    },
];

const TRUST = [
    {
        icon: ShieldCheck,
        kicker: "Safe payments",
        title: "Payments protected by Stripe",
        body: "Every transaction runs on Stripe. Your card details never touch Lendly's servers, and lenders get paid only after pickup is confirmed.",
    },
    {
        icon: BadgeCheck,
        kicker: "Verified neighbors",
        title: "Real people, real verification",
        body: "We verify ID and address for every member who lists. Profile reviews and ratings keep the community honest, friendly, and accountable.",
    },
    {
        icon: Lock,
        kicker: "Private by default",
        title: "Your address stays yours",
        body: "Exact addresses are only shared after a borrow request is confirmed. We show street names and neighborhoods — never your front door.",
    },
];

const Landing = () => {
    return (
        <main data-testid={LANDING.root} className="relative z-10">
            {/* ---------- Hero ---------- */}
            <section
                data-testid={LANDING.hero}
                className="relative overflow-hidden px-6 pt-16 pb-20 md:px-10 md:pt-24 md:pb-28"
            >
                <div
                    aria-hidden="true"
                    className="pointer-events-none absolute -top-32 -right-20 h-[420px] w-[420px] rounded-full bg-[#737a3d]/15 blur-3xl"
                />
                <div
                    aria-hidden="true"
                    className="pointer-events-none absolute -bottom-24 -left-16 h-[360px] w-[360px] rounded-full bg-[#B56C5A]/10 blur-3xl"
                />

                <div className="mx-auto grid max-w-7xl items-center gap-14 lg:grid-cols-[1.1fr_0.9fr]">
                    <div className="animate-fade-up">
                        <div className="mb-5 inline-flex items-center gap-2 rounded-full border border-[#2e3220]/10 bg-[#F4F1EA]/70 px-3 py-1.5 backdrop-blur">
                            <Sparkles className="h-3.5 w-3.5 text-[#737a3d]" strokeWidth={2.5} />
                            <span className="text-xs font-bold uppercase tracking-widest text-[#737a3d]">
                                Your Neighborhood Lending Network
                            </span>
                        </div>

                        <h1 className="font-display text-5xl font-bold leading-[1.05] tracking-tight text-[#2e3220] sm:text-6xl lg:text-[64px]">
                            The Things You Need.{" "}
                            <span className="relative inline-block">
                                <span className="relative z-10 text-[#737a3d]">Right Next Door.</span>
                                <span
                                    aria-hidden="true"
                                    className="absolute inset-x-0 bottom-1 h-3 rounded-full bg-[#8a9450]/30"
                                />
                            </span>
                        </h1>

                        <p className="mt-6 max-w-xl text-lg leading-relaxed text-[#5a6142]">
                            {BRAND.description}
                        </p>

                        <div className="mt-9 flex flex-wrap items-center gap-3">
                            <Link
                                to="/browse"
                                data-testid={LANDING.heroBrowseCta}
                                className="btn-primary"
                            >
                                Browse Items
                                <ArrowRight className="h-4 w-4" strokeWidth={2.5} />
                            </Link>
                            <Link
                                to="/list"
                                data-testid={LANDING.heroListCta}
                                className="btn-secondary"
                            >
                                List Your First Item
                            </Link>
                            <Link
                                to="/how-it-works"
                                data-testid="landing-how-it-works-link"
                                className="text-sm font-semibold text-[#737a3d] underline underline-offset-4 hover:text-[#2e3220]"
                            >
                                See how it works →
                            </Link>
                        </div>

                        <div className="mt-10 flex items-center gap-6 text-sm text-[#5a6142]">
                            <div className="flex -space-x-2">
                                <span className="h-8 w-8 rounded-full border-2 border-[#F4F1EA] bg-[#8a9450]" />
                                <span className="h-8 w-8 rounded-full border-2 border-[#F4F1EA] bg-[#B56C5A]" />
                                <span className="h-8 w-8 rounded-full border-2 border-[#F4F1EA] bg-[#2e3220]" />
                                <span className="h-8 w-8 rounded-full border-2 border-[#F4F1EA] bg-[#737a3d]" />
                            </div>
                            <span>Built for the people on your street — not for strangers across the city.</span>
                        </div>
                    </div>

                    <div className="relative animate-fade-up [animation-delay:120ms]">
                        <div className="relative aspect-square w-full max-w-md mx-auto lg:max-w-none">
                            <div className="absolute inset-0 rounded-[40px] bg-gradient-to-br from-[#E8E2D0] to-[#d5cfb8] border border-[#2e3220]/10 shadow-[0_30px_80px_rgba(46,50,32,0.12)]" />
                            <div className="absolute inset-0 flex items-center justify-center p-10">
                                <img
                                    src={LOGO_URL}
                                    alt="Lendly mark"
                                    className="h-full w-full object-contain drop-shadow-[0_6px_24px_rgba(46,50,32,0.15)]"
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* ---------- How it works ---------- */}
            <section
                data-testid={LANDING.howItWorks}
                className="px-6 pb-24 md:px-10"
            >
                <div className="mx-auto max-w-7xl">
                    <div className="mb-12 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
                        <div>
                            <p className="text-xs font-bold uppercase tracking-widest text-[#737a3d]">
                                How it works
                            </p>
                            <h2 className="mt-2 font-display text-3xl font-bold text-[#2e3220] sm:text-4xl">
                                Three simple steps to become a Lendly Neighbor.
                            </h2>
                        </div>
                        <p className="max-w-md text-sm leading-relaxed text-[#5a6142]">
                            No warehouses, no shipping labels, no strangers. Just neighbors
                            helping each other do more with less.
                        </p>
                    </div>

                    <div className="grid gap-6 md:grid-cols-3">
                        {STEPS.map((s, i) => {
                            const Icon = s.icon;
                            return (
                                <div
                                    key={s.title}
                                    data-testid={LANDING.howStep(i + 1)}
                                    className="card-surface relative p-7 animate-fade-up"
                                    style={{ animationDelay: `${i * 90}ms` }}
                                >
                                    <span className="absolute right-6 top-6 font-display text-5xl font-bold text-[#2e3220]/10">
                                        0{i + 1}
                                    </span>
                                    <div className="mb-5 inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-[#737a3d] text-[#F4F1EA]">
                                        <Icon className="h-5 w-5" strokeWidth={2.25} />
                                    </div>
                                    <p className="text-xs font-bold uppercase tracking-widest text-[#737a3d]">
                                        {s.kicker}
                                    </p>
                                    <h3 className="mt-1 font-display text-xl font-bold text-[#2e3220]">
                                        {s.title}
                                    </h3>
                                    <p className="mt-2 text-sm leading-relaxed text-[#5a6142]">
                                        {s.body}
                                    </p>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* ---------- Why Lendly ---------- */}
            <section
                data-testid={LANDING.whyLendly}
                className="bg-[#E8E2D0]/40 px-6 py-24 md:px-10"
            >
                <div className="mx-auto max-w-7xl">
                    <div className="mb-12 grid gap-8 md:grid-cols-[1fr_1.4fr] md:items-end">
                        <div>
                            <p className="text-xs font-bold uppercase tracking-widest text-[#737a3d]">
                                Why Lendly
                            </p>
                            <h2 className="mt-2 font-display text-3xl font-bold text-[#2e3220] sm:text-4xl">
                                Save money. Reduce waste. Build community.
                            </h2>
                        </div>
                        <p className="max-w-xl justify-self-end text-base leading-relaxed text-[#5a6142]">
                            Lendly was built for people who&apos;d rather borrow than buy, share
                            than store, and meet a neighbor than scroll past another ad.
                            Three things happen when a block starts lending.
                        </p>
                    </div>

                    <div className="grid gap-6 md:grid-cols-3">
                        {BENEFITS.map((b, i) => {
                            const Icon = b.icon;
                            return (
                                <div
                                    key={b.title}
                                    data-testid={LANDING.benefit(i + 1)}
                                    className="rounded-3xl border border-[#2e3220]/10 bg-[#F4F1EA] p-7 transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_8px_30px_rgba(46,50,32,0.08)] animate-fade-up"
                                    style={{ animationDelay: `${i * 90}ms` }}
                                >
                                    <div className="mb-5 inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-[#2e3220] text-[#F4F1EA]">
                                        <Icon className="h-5 w-5" strokeWidth={2.25} />
                                    </div>
                                    <h3 className="font-display text-xl font-bold text-[#2e3220]">
                                        {b.title}
                                    </h3>
                                    <p className="mt-2 text-sm leading-relaxed text-[#5a6142]">
                                        {b.body}
                                    </p>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* ---------- Trust ---------- */}
            <section
                data-testid={LANDING.trust}
                className="px-6 py-24 md:px-10"
            >
                <div className="mx-auto max-w-7xl">
                    <div className="mb-12 max-w-2xl">
                        <p className="text-xs font-bold uppercase tracking-widest text-[#737a3d]">
                            Trust & safety
                        </p>
                        <h2 className="mt-2 font-display text-3xl font-bold text-[#2e3220] sm:text-4xl">
                            Friendly marketplace. Serious safeguards.
                        </h2>
                        <p className="mt-4 text-base leading-relaxed text-[#5a6142]">
                            Lending between neighbors only works when everyone feels safe.
                            We built Lendly with real verification, protected payments, and
                            privacy that doesn&apos;t ask you to over-share.
                        </p>
                    </div>

                    <div className="grid gap-6 md:grid-cols-3">
                        {TRUST.map((t, i) => {
                            const Icon = t.icon;
                            return (
                                <div
                                    key={t.title}
                                    data-testid={LANDING.trustItem(i + 1)}
                                    className="card-surface flex h-full flex-col p-7 animate-fade-up"
                                    style={{ animationDelay: `${i * 90}ms` }}
                                >
                                    <div className="mb-5 inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-[#737a3d]/15 text-[#737a3d]">
                                        <Icon className="h-5 w-5" strokeWidth={2.25} />
                                    </div>
                                    <p className="text-xs font-bold uppercase tracking-widest text-[#737a3d]">
                                        {t.kicker}
                                    </p>
                                    <h3 className="mt-1 font-display text-lg font-bold text-[#2e3220]">
                                        {t.title}
                                    </h3>
                                    <p className="mt-2 text-sm leading-relaxed text-[#5a6142]">
                                        {t.body}
                                    </p>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* ---------- Final CTA ---------- */}
            <section
                data-testid={LANDING.finalCta}
                className="px-6 pb-24 md:px-10"
            >
                <div className="mx-auto max-w-5xl rounded-[40px] bg-[#2e3220] px-8 py-14 text-center text-[#F4F1EA] md:px-16 md:py-20">
                    <div className="mb-5 inline-flex items-center gap-2 rounded-full bg-[#F4F1EA]/10 px-3 py-1.5">
                        <Sprout className="h-3.5 w-3.5 text-[#8a9450]" strokeWidth={2.5} />
                        <span className="text-xs font-bold uppercase tracking-widest text-[#8a9450]">
                            Your block. Your stuff. Your community.
                        </span>
                    </div>
                    <h2 className="font-display text-4xl font-bold leading-tight tracking-tight sm:text-5xl">
                        Become a Lendly Neighbor.
                    </h2>
                    <p className="mx-auto mt-4 max-w-xl text-base leading-relaxed text-[#F4F1EA]/75">
                        Whether you need a drill for the weekend or want to lend out the gear
                        sitting in your garage — Lendly is built for you.
                    </p>
                    <div className="mt-9 flex flex-wrap items-center justify-center gap-3">
                        <Link
                            to="/browse"
                            data-testid={LANDING.finalCtaBrowse}
                            className="inline-flex items-center justify-center gap-2 rounded-full bg-[#F4F1EA] px-7 py-3 text-base font-bold text-[#2e3220] transition-all duration-200 hover:-translate-y-0.5 hover:bg-white"
                        >
                            Browse Items
                            <ArrowRight className="h-4 w-4" strokeWidth={2.5} />
                        </Link>
                        <Link
                            to="/list"
                            data-testid={LANDING.finalCtaList}
                            className="inline-flex items-center justify-center gap-2 rounded-full border-2 border-[#F4F1EA] px-7 py-[10px] text-base font-bold text-[#F4F1EA] transition-all duration-200 hover:-translate-y-0.5 hover:bg-[#F4F1EA]/10"
                        >
                            List Your First Item
                        </Link>
                    </div>
                </div>
            </section>
        </main>
    );
};

export default Landing;
